import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Eye, EyeOff, AlertCircle, Mail, ArrowLeft, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_bab98220-a396-492d-90c6-b545ef026af6/artifacts/jwqj5vlw_logo_transparent.de5a7eae8bd4251a0668.png';

// Helper to extract error message from API response
const getErrorMessage = (err, fallback = 'An error occurred') => {
  const detail = err?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg;
  if (typeof detail === 'object' && detail.msg) return detail.msg;
  return fallback;
};

// Generate or retrieve device ID
const getDeviceId = () => {
  let deviceId = localStorage.getItem('deviceId');
  if (!deviceId) {
    deviceId = 'device_' + Math.random().toString(36).substring(2) + Date.now().toString(36);
    localStorage.setItem('deviceId', deviceId);
  }
  return deviceId;
};

export const LoginPage = () => {
  const navigate = useNavigate();
  const { login, isAuthenticated, setUser, setToken } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // OTP State
  const [showOTPForm, setShowOTPForm] = useState(false);
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [tempToken, setTempToken] = useState('');
  const [rememberDevice, setRememberDevice] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);
  
  const otpInputRefs = useRef([]);

  useEffect(() => {
    if (resendCountdown > 0) {
      const timer = setTimeout(() => setResendCountdown(resendCountdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCountdown]);

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const deviceId = getDeviceId();
      const response = await api.post('/auth/login', { 
        email, 
        password,
        deviceId 
      });
      
      // Check if OTP is required
      if (response.data.requiresOTP) {
        setTempToken(response.data.tempToken);
        setShowOTPForm(true);
        setResendCountdown(60); // 60 second cooldown for resend
        toast.success('Verification code sent to your email');
      } else {
        // Direct login (trusted device)
        const { token, user } = response.data;
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(user));
        setToken(token);
        setUser(user);
        toast.success(`Welcome back, ${user.name}!`);
        navigate('/dashboard');
      }
    } catch (err) {
      setError(getErrorMessage(err, 'Invalid email or password'));
    } finally {
      setLoading(false);
    }
  };

  const handleOTPChange = (index, value) => {
    if (value.length > 1) {
      // Handle paste
      const pastedValue = value.slice(0, 6).split('');
      const newOtp = [...otp];
      pastedValue.forEach((char, i) => {
        if (index + i < 6) {
          newOtp[index + i] = char;
        }
      });
      setOtp(newOtp);
      // Focus on the next empty input or last input
      const nextIndex = Math.min(index + pastedValue.length, 5);
      otpInputRefs.current[nextIndex]?.focus();
      return;
    }
    
    if (!/^\d*$/.test(value)) return; // Only allow digits
    
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    
    // Auto-focus next input
    if (value && index < 5) {
      otpInputRefs.current[index + 1]?.focus();
    }
  };

  const handleOTPKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpInputRefs.current[index - 1]?.focus();
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const otpCode = otp.join('');
    if (otpCode.length !== 6) {
      setError('Please enter the complete 6-digit code');
      setLoading(false);
      return;
    }

    try {
      const deviceId = getDeviceId();
      const response = await api.post('/auth/verify-otp', {
        email,
        otp: otpCode,
        tempToken,
        rememberDevice,
        deviceId
      });
      
      const { token, user } = response.data;
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      setToken(token);
      setUser(user);
      toast.success(`Welcome back, ${user.name}!`);
      navigate('/dashboard');
    } catch (err) {
      setError(getErrorMessage(err, 'Invalid verification code'));
      setOtp(['', '', '', '', '', '']);
      otpInputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (resendCountdown > 0) return;
    
    setResendLoading(true);
    try {
      const formData = new FormData();
      formData.append('email', email);
      formData.append('tempToken', tempToken);
      
      await api.post('/auth/resend-otp', formData);
      toast.success('New verification code sent to your email');
      setResendCountdown(60);
      setOtp(['', '', '', '', '', '']);
      otpInputRefs.current[0]?.focus();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to resend code'));
    } finally {
      setResendLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setShowOTPForm(false);
    setOtp(['', '', '', '', '', '']);
    setTempToken('');
    setError('');
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Branding */}
      <div
        className="hidden lg:flex lg:w-1/2 relative bg-cover bg-center"
        style={{
          backgroundImage: 'url(https://images.pexels.com/photos/18435276/pexels-photo-18435276.jpeg)',
        }}
      >
        <div className="absolute inset-0 bg-[#6a5da4]/85" />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <img 
              src={LOGO_URL} 
              alt="Al Arabia" 
              className="h-16 w-auto brightness-0 invert"
            />
          </div>
          
          <div className="space-y-6">
            <h1 className="font-heading text-4xl md:text-5xl font-light tracking-tight leading-tight">
              Streamline Your<br />
              <span className="font-semibold">Payment Approvals</span>
            </h1>
            <p className="text-white/80 text-lg max-w-md">
              Efficient voucher management with multi-level approval workflow. 
              Track payments from creation to completion.
            </p>
          </div>

          <p className="text-white/60 text-sm">
            © 2026 Arabian Contracting Services. All rights reserved.
          </p>
        </div>
      </div>

      {/* Right Panel - Login/OTP Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-[#FAFAFA]">
        <div className="w-full max-w-md space-y-8">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center justify-center mb-8">
            <img 
              src={LOGO_URL} 
              alt="Al Arabia" 
              className="h-20 w-auto"
            />
          </div>

          {!showOTPForm ? (
            /* Login Form */
            <Card className="border-0 shadow-xl">
              <CardHeader className="space-y-1 pb-4">
                <CardTitle className="text-2xl font-heading font-semibold">Sign in</CardTitle>
                <CardDescription>
                  Enter your credentials to access the system
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  {error && (
                    <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm animate-fade-in">
                      <AlertCircle className="w-4 h-4 flex-shrink-0" />
                      <span>{error}</span>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="name@company.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      data-testid="email-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="password">Password</Label>
                    <div className="relative">
                      <Input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="••••••••"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        data-testid="password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-[#6a5da4] hover:bg-[#5a4d94] h-11"
                    disabled={loading}
                    data-testid="login-button"
                  >
                    {loading ? 'Signing in...' : 'Sign in'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          ) : (
            /* OTP Verification Form */
            <Card className="border-0 shadow-xl">
              <CardHeader className="space-y-1 pb-4">
                <button
                  onClick={handleBackToLogin}
                  className="flex items-center text-sm text-gray-500 hover:text-gray-700 mb-2"
                >
                  <ArrowLeft className="w-4 h-4 mr-1" />
                  Back to login
                </button>
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-[#6a5da4]/10 rounded-full">
                    <Mail className="w-6 h-6 text-[#6a5da4]" />
                  </div>
                  <div>
                    <CardTitle className="text-2xl font-heading font-semibold">Verify Email</CardTitle>
                    <CardDescription>
                      Enter the 6-digit code sent to<br />
                      <span className="font-medium text-gray-700">{email}</span>
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleVerifyOTP} className="space-y-6">
                  {error && (
                    <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm animate-fade-in">
                      <AlertCircle className="w-4 h-4 flex-shrink-0" />
                      <span>{error}</span>
                    </div>
                  )}

                  {/* OTP Input */}
                  <div className="flex justify-center gap-2">
                    {otp.map((digit, index) => (
                      <Input
                        key={index}
                        ref={(el) => (otpInputRefs.current[index] = el)}
                        type="text"
                        inputMode="numeric"
                        maxLength={6}
                        value={digit}
                        onChange={(e) => handleOTPChange(index, e.target.value)}
                        onKeyDown={(e) => handleOTPKeyDown(index, e)}
                        className="w-12 h-14 text-center text-2xl font-semibold border-2 focus:border-[#6a5da4] focus:ring-[#6a5da4]"
                        data-testid={`otp-input-${index}`}
                      />
                    ))}
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-[#6a5da4] hover:bg-[#5a4d94] h-11"
                    disabled={loading || otp.join('').length !== 6}
                    data-testid="verify-otp-button"
                  >
                    {loading ? 'Verifying...' : 'Verify & Sign in'}
                  </Button>

                  {/* Resend OTP */}
                  <div className="text-center">
                    <p className="text-sm text-gray-500 mb-2">
                      Didn't receive the code?
                    </p>
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={handleResendOTP}
                      disabled={resendCountdown > 0 || resendLoading}
                      className="text-[#6a5da4] hover:text-[#5a4d94]"
                      data-testid="resend-otp-button"
                    >
                      {resendLoading ? (
                        <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                      ) : null}
                      {resendCountdown > 0 
                        ? `Resend code in ${resendCountdown}s` 
                        : 'Resend code'}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
